import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type APIKey, timeAgo } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, ErrorBox, Badge } from '../components/ui'
import { IconPlus, IconTrash } from '../components/icons'

export function APIKeysPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [newKey, setNewKey] = useState<{ key: string; name: string } | null>(null)

  const { data: keys = [] } = useQuery({ queryKey: ['api-keys'], queryFn: async () => (await api.get<APIKey[]>('/api-keys')).data })

  const revoke = useMutation({
    mutationFn: async (id: number) => api.post(`/api-keys/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })

  return (
    <>
      <PageHeader
        title="API-ключи"
        subtitle="Используйте для интеграций и автоматизации. Отправляйте как заголовок Authorization: Bearer bh_..."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Создать ключ</Button>}
      />
      <Table<APIKey>
        rows={keys}
        empty="Нет API-ключей."
        cols={[
          { key: 'n', label: 'Имя', render: (k) => <span className="font-medium">{k.name}</span> },
          { key: 'p', label: 'Префикс',
            render: (k) => <span className="font-mono text-[12px] text-neutral-600">{k.key_prefix}…</span> },
          { key: 's', label: 'Статус',
            render: (k) => k.revoked_at ? <Badge tone="red">отозван</Badge> : <Badge tone="green">активен</Badge> },
          { key: 'u', label: 'Последнее использование',
            render: (k) => <span className="text-[12px] text-neutral-500">{k.last_used_at ? timeAgo(k.last_used_at) : 'ни разу'}</span> },
          { key: 'e', label: 'Истекает',
            render: (k) => <span className="text-[12px] text-neutral-500">
              {k.expires_at ? new Date(k.expires_at).toLocaleDateString('ru-RU') : 'бессрочный'}
            </span> },
          { key: 'c', label: 'Создан',
            render: (k) => <span className="text-[12px] text-neutral-500">{timeAgo(k.created_at)}</span> },
          { key: 'act', label: '', w: '1%',
            render: (k) => (
              <div className="flex gap-1 justify-end">
                {!k.revoked_at && (
                  <Button variant="ghost" size="sm" onClick={() => confirm(`Отозвать ключ "${k.name}"?`) && revoke.mutate(k.id)}>
                    Отозвать
                  </Button>
                )}
                <Button variant="ghost" size="sm" title="Удалить"
                        onClick={() => confirm(`Удалить ключ "${k.name}" навсегда?`) && del.mutate(k.id)}>
                  <IconTrash />
                </Button>
              </div>
            )},
        ]}
      />
      {addOpen && <CreateKeyModal onClose={() => setAddOpen(false)}
        onCreated={(k, name) => { setAddOpen(false); setNewKey({ key: k, name }); qc.invalidateQueries({ queryKey: ['api-keys'] }) }} />}
      {newKey && <ShowKeyModal keyValue={newKey.key} name={newKey.name} onClose={() => setNewKey(null)} />}
    </>
  )
}

function CreateKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string, name: string) => void }) {
  const [f, setF] = useState({ name: '', expires_days: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => {
      const body: any = { name: f.name }
      if (f.expires_days) {
        const d = new Date()
        d.setDate(d.getDate() + parseInt(f.expires_days, 10))
        body.expires_at = d.toISOString()
      }
      return (await api.post('/api-keys', body)).data
    },
    onSuccess: (r: any) => onCreated(r.key, r.api_key.name),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Создать API-ключ">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Имя ключа" hint="Просто для идентификации. Например: My CI script">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="My CI script" required autoFocus />
        </Field>
        <Field label="Срок действия (дней)" hint="Оставьте пустым — бессрочный">
          <Input type="number" value={f.expires_days} onChange={(e) => setF({ ...f, expires_days: e.target.value })} placeholder="365" />
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>Создать</Button>
        </div>
      </form>
    </Modal>
  )
}

function ShowKeyModal({ keyValue, name, onClose }: { keyValue: string; name: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(keyValue)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
      const ta = document.createElement('textarea')
      ta.value = keyValue
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    }
  }
  return (
    <Modal open onClose={onClose} title={`Ключ "${name}" создан`}>
      <div className="space-y-4">
        <div className="p-3 rounded-md bg-yellow-50 border border-yellow-200 text-[13px] text-yellow-800">
          <strong>Сохраните ключ сейчас.</strong> После закрытия окна показать его снова будет нельзя.
        </div>
        <div>
          <div className="text-[12px] font-medium text-neutral-700 mb-1.5">Ваш ключ</div>
          <div className="flex gap-2">
            <input readOnly value={keyValue}
                   className="flex-1 border border-neutral-300 rounded-md px-3 py-2 text-[12px] font-mono bg-neutral-50" />
            <Button variant="secondary" onClick={copy}>{copied ? 'Скопировано ✓' : 'Скопировать'}</Button>
          </div>
        </div>
        <div>
          <div className="text-[12px] font-medium text-neutral-700 mb-1.5">Пример использования</div>
          <pre className="bg-neutral-900 text-neutral-100 text-[11px] font-mono p-3 rounded-md overflow-x-auto">
{`curl -H "Authorization: Bearer ${keyValue}" \\
  https://hosting.beebro.cloud/api/v1/nodes`}
          </pre>
        </div>
        <div className="flex justify-end pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button onClick={onClose}>Готово</Button>
        </div>
      </div>
    </Modal>
  )
}
